import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Minus, Plus } from 'lucide-react';
import { type UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import Close from '@/assets/icons/close.svg?react';
import ArrowRight from '@/assets/icons/arrow-right.svg?react';
import SalesOrderLineInterface from '@/interfaces/SalesOrderLineInterface';
import LinesUtility from '@/utility/lines-utility';
import Edit from '../../../assets/icons/edit.svg?react';
import Checkmark from '../../../assets/icons/checkmark.svg?react';
import PlaceholderImage from '@/assets/images/placeholder.svg';
import RecipeInterface from '@/interfaces/Recipe';
import { ZoomIn } from 'lucide-react';
import ProductPreviewModal from '../product-preview-modal/product-preview-modal';

interface QuantityModalProps {
  open: boolean;
  line: SalesOrderLineInterface;
  lines: SalesOrderLineInterface[];
  previousRecipes: RecipeInterface[];
  maxQuantity?: number;
  onClose: () => void;
  onContinue: (line: SalesOrderLineInterface, quantity: number, previousDesign: RecipeInterface | null) => void;
}

const initialQuantity = (maximumQuantity: number) => String(Math.max(0, maximumQuantity));

export default function QuantityModal({ open, line, lines, previousRecipes, onClose, onContinue }: QuantityModalProps) {
  const maximumQuantity = line.Line_To_Change
    ? +line.Quantity
    : LinesUtility.remainingPersonalisationQuantity(lines, line);
  const productName = line.Sold_As_Description || line.Description || 'Product awaiting details';
  // const previousRecipes = useMemo(() => {
  //   const firstPreviousDesign = LinesUtility.personalisedLines(lines, line)[0] ?? lines[0] ?? line;

  //   // Temporary carousel data until saved designs are supplied by the API.
  //   return Array.from({ length: 1 }, () => firstPreviousDesign);
  // }, [line, lines]);
  const carouselViewportRef = useRef<HTMLDivElement>(null);
  const carouselTrackRef = useRef<HTMLDivElement>(null);
  const pendingDesignIndexRef = useRef<number | null>(null);

  const [activeDesignIndex, setActiveDesignIndex] = useState(0);
  const [selectedPreviousDesign, setSelectedPreviousDesign] = useState<RecipeInterface | null>(null);
  const [selectedDesignIndex, setSelectedDesignIndex] = useState<number | null>(null);
  const [isNewDesignSelected, setIsNewDesignSelected] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [quantity, setQuantity] = useState(() => initialQuantity(maximumQuantity));
  const haspreviousRecipes = previousRecipes.length > 0;

  const settleOnClosestDesign = useCallback((carouselViewport: HTMLDivElement) => {
    const designCards = Array.from(
      carouselTrackRef.current?.querySelectorAll<HTMLElement>('[data-design-index]') ?? [],
    );

    if (!designCards.length) return;

    const viewportCenter = carouselViewport.scrollLeft + carouselViewport.clientWidth / 2;
    const closestDesign = designCards.reduce((closest, designCard) => {
      const cardCenter = designCard.offsetLeft + designCard.offsetWidth / 2;
      const closestCenter = closest.offsetLeft + closest.offsetWidth / 2;

      return Math.abs(cardCenter - viewportCenter) < Math.abs(closestCenter - viewportCenter) ? designCard : closest;
    });
    const nextIndex = Number(closestDesign.dataset.designIndex);

    if (Number.isInteger(nextIndex)) {
      setActiveDesignIndex(nextIndex);
    }

    pendingDesignIndexRef.current = null;
  }, []);

  useEffect(() => {
    setQuantity(initialQuantity(maximumQuantity));
    setActiveDesignIndex(0);
    setSelectedPreviousDesign(null);
    setSelectedDesignIndex(null);
    setIsNewDesignSelected(false);
    pendingDesignIndexRef.current = null;

    const carouselViewport = carouselViewportRef.current;
    if (carouselViewport && typeof carouselViewport.scrollTo === 'function') {
      carouselViewport.scrollTo({ left: 0 });
    }
  }, [line.Line_No, maximumQuantity, open]);

  useEffect(() => {
    const carouselViewport = carouselViewportRef.current;
    if (!carouselViewport || !open) return;

    const handleScrollEnd = () => settleOnClosestDesign(carouselViewport);

    carouselViewport.addEventListener('scrollend', handleScrollEnd);

    return () => {
      carouselViewport.removeEventListener('scrollend', handleScrollEnd);
    };
  }, [open, settleOnClosestDesign]);

  useEffect(() => {
    setIsNewDesignSelected(true);
  }, [open]);

  useEffect(() => {
    if (open && line?.Line_To_Change === true) setQuantity(line.Quantity);
  }, [open, line.Line_To_Change]);

  const scrollToDesign = (index: number) => {
    const wrappedIndex = (index + previousRecipes.length) % previousRecipes.length;
    const carouselViewport = carouselViewportRef.current;
    const designCard = carouselTrackRef.current?.querySelector<HTMLElement>(`[data-design-index="${wrappedIndex}"]`);

    if (wrappedIndex === activeDesignIndex && pendingDesignIndexRef.current === null) return;

    pendingDesignIndexRef.current = wrappedIndex;

    if (carouselViewport && designCard && typeof carouselViewport.scrollTo === 'function') {
      carouselViewport.scrollTo({
        behavior: 'smooth',
        left: designCard.offsetLeft - (carouselViewport.clientWidth - designCard.offsetWidth) / 2,
      });
    }
  };

  const handleDesignSelection = (index: number, design: RecipeInterface) => {
    setSelectedDesignIndex(index);
    setSelectedPreviousDesign(design);
    setIsNewDesignSelected(false);

    if (activeDesignIndex !== index || pendingDesignIndexRef.current !== null) {
      scrollToDesign(index);
    }
  };

  const handleNewDesignSelection = () => {
    setIsNewDesignSelected(true);
    setSelectedPreviousDesign(null);
    setSelectedDesignIndex(null);
  };

  const handleDesignScroll = (event: UIEvent<HTMLDivElement>) => {
    settleOnClosestDesign(event.currentTarget);
  };

  const clampQuantity = (value: number) => Math.min(maximumQuantity, Math.max(1, Math.trunc(value)));

  const updateQuantity = (value: number) => {
    if (Number.isNaN(value)) return;
    setQuantity(String(clampQuantity(value)));
  };

  // const moveDesign = (direction: -1 | 1) => {
  //   const currentIndex = pendingDesignIndexRef.current ?? activeDesignIndex;
  //   scrollToDesign(currentIndex + direction);
  // };

  const numericQuantity = Number.parseInt(quantity, 10);
  const hasQuantity = Number.isFinite(numericQuantity);
  const hasValidQuantity = hasQuantity && numericQuantity > 0 && numericQuantity <= maximumQuantity;
  const isContinueButtonDisabled = hasValidQuantity === false || (!isNewDesignSelected && !selectedPreviousDesign);

  const handleZoomInButtonClick = (event: React.MouseEvent<HTMLButtonElement>, imageUrl: string = '') => {
    event.stopPropagation();

    setShowPreviewModal(true);
    setImagePreview(imageUrl);
  };

  const handlePreviewModalClose = () => {
    setShowPreviewModal(false);
    setImagePreview('');
  };

  return (
    <AlertDialogPrimitive.Root open={open}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]" />

        <AlertDialogPrimitive.Content
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            onClose();
          }}
          className="fixed left-1/2 top-1/2 z-[51] max-h-[calc(100dvh_-_16px)] w-[calc(100%_-_16px)] max-w-[520px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto overscroll-contain border border-screwfix-grey-2 bg-background px-[18px] pb-[24px] pt-[22px] shadow-[0_16px_45px_rgba(10,10,10,0.2)] outline-none sm:max-h-[calc(100dvh_-_32px)] sm:w-[calc(100%_-_32px)] sm:px-[26px] sm:pb-[28px] sm:pt-[26px] lg:max-w-[1000px] xl:max-w-[1200px]"
        >
          <header className="relative">
            <AlertDialogPrimitive.Title className="max-w-[390px] text-[28px] font-bold leading-[31px] tracking-[-0.02em] sm:text-[32px] sm:leading-[35px] mt-[-5px]">
              {line.Line_To_Change !== true && <span>Personalise your product</span>}
              {line.Line_To_Change === true && <span>Change personalisation</span>}
            </AlertDialogPrimitive.Title>
            <AlertDialogPrimitive.Description className="sr-only">
              Choose a saved design and the number of pieces to personalise.
            </AlertDialogPrimitive.Description>

            <div className="absolute right-5 top-[-30px] flex w-full flex-row items-center justify-end">
              <button
                type="button"
                className="flex flex-row items-center justify-end text-white text-[clamp(17px,1.5vw,18px)]"
                onClick={onClose}
              >
                CLOSE
                <Close fill="white" className="h-[20px]" />
              </button>
            </div>

            <AlertDialogPrimitive.Cancel asChild>
              <button
                aria-label="Close quantity selection"
                className="absolute top-3 right-0 fill-blue-600 cursor-pointer"
                onClick={onClose}
                type="button"
              >
                <Close fill="black" className="h-[28px]" />
              </button>
            </AlertDialogPrimitive.Cancel>

            <div className="flex flex-row w-full mt-10">
              <div className="border items-center justify-center h-[80px] w-[80px] shrink-0 border-screwfix-grey-2 bg-background p-[2px] rounded-2xl shadow-[0_1px_4px_rgba(10,10,10,0.03)] border-screwfix-grey-1">
                {/* <img src={line.Product_URL_1} alt={productName} className="h-full w-full object-contain" /> */}

                <img
                  className="h-full w-full object-contain"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = PlaceholderImage;
                  }}
                  src={line.Product_URL_1 || PlaceholderImage}
                  alt={productName}
                />
              </div>
              <div className="flex flex-col flex-1 justify-center items-start pl-10">
                <p className="[font-family:archivo_narrowbold,sans-serif] text-[17px] font-bold leading-[20px] text-screwfix-blue-shade-3 sm:text-[17px]">
                  {productName}
                </p>
                {line.Line_To_Change !== true && (
                  <p className="mt-[3px] [font-family:archivo_narrowregular,sans-serif] text-[16px] font-normal leading-[19px] text-screwfix-grey-4 sm:text-[18px]">
                    {maximumQuantity} {maximumQuantity === 1 ? 'piece' : 'pieces'} still need personalisation
                  </p>
                )}
              </div>
            </div>
          </header>

          {/* QUANTITY INPUT */}
          <section aria-labelledby="quantity-heading" className="mt-[22px]">
            <div className="my-[22px] border-t border-dashed border-[#e5e5e5]" />

            <div className="flex items-center justify-start gap-[14px]">
              {haspreviousRecipes && (
                <div className="text-[clamp(17px,calc(15px_+_0.3vw),18px)] flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#dfeeff]">
                  <span>1</span>
                </div>
              )}
              <div>
                <h2 className="text-[16px] font-bold uppercase leading-[17px] tracking-[0.025em]" id="quantity-heading">
                  <b>How many pieces?</b>
                </h2>
                <p className="mt-[3px] [font-family:archivo_narrowregular,sans-serif] text-[16px] font-normal leading-[18px] text-screwfix-grey-4 sm:text-[17px]">
                  {/* <span>Choose how many pieces you want to personalise.</span> */}
                  {/* <span>Choose how many pieces you want to personalise.</span> */}
                  {line.Line_To_Change !== true && (
                    <span>
                      Choose how many pieces to personalise together. You can personalise the rest differently later.
                    </span>
                  )}
                  {line.Line_To_Change === true && (
                    <span>
                      You will change personalisation for {quantity} {+quantity === 1 ? 'piece' : 'pieces'}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-1 items-center justify-center">
              <div
                className={`mt-[17px] flex w-full items-center justify-center overflow-hidden rounded-[6px] border border-screwfix-grey-2 bg-background lg:max-w-[60%]`}
              >
                {line.Line_To_Change !== true && (
                  <button
                    aria-label="Decrease quantity"
                    className="button-screwfix-secondary flex h-[46px] w-[54px] shrink-0 items-center justify-center !rounded-none !border-0 !border-r !border-screwfix-grey-2 !p-0 disabled:cursor-not-allowed"
                    disabled={!hasQuantity || numericQuantity <= 1}
                    onClick={() => updateQuantity(numericQuantity - 1)}
                    type="button"
                  >
                    <Minus
                      aria-hidden="true"
                      className="h-[22px] w-[22px] text-screwfix-blue-shade-3"
                      strokeWidth={2.5}
                    />
                  </button>
                )}
                <input
                  aria-label="Quantity to personalise"
                  className={`h-[46px] min-w-0 flex-1 border-0 bg-background px-[14px] text-center [font-family:archivo_narrowbold,sans-serif] text-[clamp(24px,calc(22px_+_0.3vw),25px)] font-bold text-screwfix-blue-shade-3 outline-none [appearance:textfield] focus:ring-2 focus:ring-inset focus:ring-screwfix-blue-primary/35 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${line.Line_To_Change ? 'bg-gray-100' : ''}`}
                  inputMode="numeric"
                  max={maximumQuantity}
                  min={1}
                  onBlur={() => updateQuantity(hasQuantity ? numericQuantity : maximumQuantity)}
                  onChange={(event) => {
                    const nextQuantity = event.currentTarget.value;

                    if (nextQuantity === '' || /^\d+$/.test(nextQuantity)) {
                      setQuantity(nextQuantity);
                    }
                  }}
                  step={1}
                  type="number"
                  value={quantity}
                  disabled={line.Line_To_Change === true}
                />

                {line.Line_To_Change !== true && (
                  <button
                    aria-label="Increase quantity"
                    className="button-screwfix-secondary flex h-[46px] w-[54px] shrink-0 items-center justify-center !rounded-none !border-0 !border-r !border-screwfix-grey-2 !p-0 disabled:cursor-not-allowed"
                    disabled={!hasQuantity || numericQuantity >= maximumQuantity}
                    onClick={() => updateQuantity((hasQuantity ? numericQuantity : 0) + 1)}
                    type="button"
                  >
                    <Plus
                      aria-hidden="true"
                      className="h-[22px] w-[22px] text-screwfix-blue-shade-3"
                      strokeWidth={2.5}
                    />
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* PREVIOUS DESIGNS */}
          {haspreviousRecipes && (
            <>
              <section aria-labelledby="saved-design-heading" className="relative">
                <div className="my-[22px] border-t border-dashed border-[#e5e5e5]" />

                <div className="flex items-center justify-start gap-[14px]">
                  <div className="text-[clamp(17px,calc(15px_+_0.3vw),18px)] flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#dfeeff]">
                    <span>2</span>
                  </div>
                  <div>
                    <h2
                      className="text-[16px] font-bold uppercase leading-[17px] tracking-[0.025em]"
                      id="saved-design-heading"
                    >
                      <b>Choose design</b>
                    </h2>
                    <p className="mt-[3px] [font-family:archivo_narrowregular,sans-serif] text-[16px] font-normal leading-[18px] text-screwfix-grey-4 sm:text-[17px]">
                      Click any existing design to use it
                    </p>
                  </div>
                </div>

                <div
                  className={`@container relative mt-[16px] ${
                    previousRecipes.length === 1
                      ? '[--design-card-width:min(640px,100cqw)]'
                      : '-mx-[18px] sm:-mx-[26px] [--design-card-width:calc(100cqw_-_108px)] sm:[--design-card-width:min(400px,calc(100cqw_-_124px))] lg:[--design-card-width:min(340px,calc(100cqw_-_124px))]'
                  }`}
                >
                  <div
                    aria-label="Previously saved designs"
                    className="touch-auto cursor-grab snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth pb-[4px] [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden"
                    onScroll={handleDesignScroll}
                    ref={carouselViewportRef}
                    role="region"
                  >
                    <div
                      className="flex w-max gap-[24px] px-[calc((100cqw_-_var(--design-card-width))_/_2)]"
                      ref={carouselTrackRef}
                    >
                      {previousRecipes.map((design, index) => {
                        // const isActive = index === activeDesignIndex;
                        const isSingleDesign = previousRecipes.length === 1;
                        const isSelected =
                          index === selectedDesignIndex && selectedPreviousDesign?.recipe_id === design.recipe_id;
                        const { image: previewImage = '' } = design;

                        return (
                          <article
                            aria-label={`Saved design ${index + 1} of ${previousRecipes.length}`}
                            className={`relative flex h-[clamp(300px,42dvh,396px)] w-[var(--design-card-width)] shrink-0 snap-center items-center justify-center overflow-hidden border ${isSingleDesign ? 'lg:h-[clamp(324px,45dvh,420px)]' : ''} ${
                              isSelected
                                ? 'border-screwfix-grey-3 bg-[#f9f9f9] shadow-[inset_0_1px_0_rgba(0,0,0,0.25),inset_0_-1px_2px_rgba(0,0,0,0.08)]'
                                : 'border-screwfix-grey-2 shadow-[0_1px_4px_rgba(10,10,10,0.18)] hover:shadow-[0_2px_6px_rgba(10,10,10,0.22)]'
                            }`}
                            data-design-index={index}
                            key={`previous-design-${index}`}
                          >
                            <button
                              aria-label={`Use saved design ${index + 1}`}
                              aria-pressed={isSelected}
                              className="absolute inset-0 z-10 cursor-pointer rounded-[6px] focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-screwfix-blue-primary"
                              onClick={() => handleDesignSelection(index, design)}
                              type="button"
                            />

                            {previewImage ? (
                              <img
                                alt={`${productName} saved design ${index + 1}`}
                                className="h-full w-full object-contain p-4"
                                draggable={false}
                                src={previewImage || PlaceholderImage}
                              />
                            ) : (
                              <p className="text-center [font-family:archivo_narrowregular,sans-serif] text-[16px] font-normal text-screwfix-grey-4">
                                No saved design preview is available.
                              </p>
                            )}

                            <button
                              className="flex items-center justify-center absolute bottom-2 right-2 w-[50px] h-[50px] p-5 z-11 cursor-pointer hover:bg-[#f0ebeb]"
                              onClick={(event) => {
                                handleZoomInButtonClick(event, previewImage);
                              }}
                            >
                              <ZoomIn className="w-full h-full p-2" color="#d3d3d3" />
                            </button>

                            <span
                              className={`absolute right-[18px] top-[20px] z-20 h-[28px] w-[28px] rounded-full border border-neutral-400 shadow-[inset_0_1px_0_rgba(0,0,0,0.25)] pointer-events-none ${
                                isSelected ? 'bg-[#7bc12b]' : 'bg-white'
                              }`}
                            >
                              {isSelected && <Checkmark className="fill-white p-2" />}
                            </span>
                          </article>
                        );
                      })}
                    </div>
                  </div>

                  {/* NAVIGATION BUTTONS */}
                  {/* <div className="pointer-events-none absolute inset-y-0 left-1/2 z-20 w-[var(--design-card-width)] -translate-x-1/2">
                    <button
                      aria-label="Previous saved design"
                      className="button-screwfix-secondary pointer-events-auto absolute left-[8px] top-1/2 flex h-[56px] w-[50px] -translate-y-1/2 items-center justify-center !p-0"
                      onClick={() => moveDesign(-1)}
                      type="button"
                    >
                      <ChevronLeft aria-hidden="true" className="h-[25px] w-[25px] text-screwfix-blue-shade-3" />
                    </button>

                    <button
                      aria-label="Next saved design"
                      className="button-screwfix-secondary pointer-events-auto absolute right-[8px] top-1/2 flex h-[56px] w-[50px] -translate-y-1/2 items-center justify-center !p-0"
                      onClick={() => moveDesign(1)}
                      type="button"
                    >
                      <ChevronRight aria-hidden="true" className="h-[25px] w-[25px] text-screwfix-blue-shade-3" />
                    </button>
                  </div> */}

                  {previousRecipes.length > 1 && (
                    <div
                      aria-label="Saved design navigation"
                      className="mt-[12px] flex flex-wrap items-center justify-center px-[18px] sm:px-[26px]"
                      role="group"
                    >
                      {previousRecipes.map((_, index) => (
                        <button
                          aria-label={`Show saved design ${index + 1} of ${previousRecipes.length}`}
                          aria-current={index === activeDesignIndex ? 'true' : undefined}
                          className="group flex h-[44px] w-[44px] cursor-pointer items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-screwfix-blue-primary"
                          key={`design-position-${index}`}
                          onClick={() => {
                            scrollToDesign(index);
                          }}
                          type="button"
                        >
                          <span
                            aria-hidden="true"
                            className={`h-[14px] rounded-full transition-[width,background-color] duration-200 motion-reduce:transition-none ${
                              index === activeDesignIndex
                                ? 'w-[14px] bg-[#0053a0]'
                                : 'w-[14px] bg-gray-300 group-hover:bg-gray-400'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </section>
              <section aria-labelledby="new-design-heading" className="relative">
                <div className="flex items-center gap-[14px] py-[22px]">
                  <div className="flex-1 border-t border-dashed border-[#e5e5e5]" />
                  <span className="[font-family:archivo_narrowbold,sans-serif] text-[16px] font-bold text-screwfix-grey-4">
                    OR
                  </span>
                  <div className="flex-1 border-t border-dashed border-[#e5e5e5]" />
                </div>

                <div className="flex justify-center">
                  <button
                    aria-pressed={isNewDesignSelected}
                    className={`group flex min-h-[116px] w-full max-w-[640px] items-center gap-[16px] border px-[16px] py-[16px] text-left sm:gap-[18px] sm:px-[18px] cursor-pointer ${
                      isNewDesignSelected
                        ? 'border-screwfix-grey-3 bg-[#f9f9f9] shadow-[inset_0_1px_0_rgba(0,0,0,0.25),inset_0_-1px_2px_rgba(0,0,0,0.08)]'
                        : 'border-screwfix-grey-2 shadow-[0_1px_4px_rgba(10,10,10,0.18)] hover:shadow-[0_2px_6px_rgba(10,10,10,0.22)]'
                    }`}
                    onClick={handleNewDesignSelection}
                    type="button"
                  >
                    <span className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full bg-[#dfeeff] sm:h-[72px] sm:w-[72px]">
                      <Edit aria-hidden="true" className="h-[32px] w-[32px] fill-[#0870df]" />
                    </span>

                    <span className="min-w-0 shrink-0 flex-1">
                      <span className="block text-[19px] font-bold leading-[22px] sm:text-[21px] sm:leading-[24px]">
                        Create new design
                      </span>
                      <span className="mt-[5px] block [font-family:archivo_narrowregular,sans-serif] text-[16px] font-normal text-[#607698] sm:text-[18px] sm:leading-[21px]">
                        Create your own personalisation for this product.
                      </span>
                    </span>

                    <span
                      className={`z-20 h-[28px] w-[28px] rounded-full border border-neutral-400 shadow-[inset_0_1px_0_rgba(0,0,0,0.25)] ${
                        isNewDesignSelected ? 'bg-[#7bc12b]' : 'bg-white'
                      }`}
                    >
                      {isNewDesignSelected && <Checkmark className="fill-white p-2" />}
                    </span>
                  </button>
                </div>
              </section>
            </>
          )}

          {/* NEW DESIGN */}
          <div className="my-[22px] border-t border-dashed border-[#e5e5e5]" />

          <footer className="flex flex-col justify-center items-end">
            <button
              className="inline-flex min-h-[42px] flex-1 w-full sm:w-[30%] items-center justify-start gap-[12px] rounded-[6px] bg-blue-600 px-[18px] text-[16px] font-bold button-screwfix-success has-icon"
              disabled={isContinueButtonDisabled}
              onClick={() => onContinue(line, numericQuantity, selectedPreviousDesign)}
              type="button"
            >
              {haspreviousRecipes && (
                <>
                  {selectedPreviousDesign !== null ? (
                    <span className="flex-1 text-center mr-[-32px]">Use this design</span>
                  ) : (
                    <span className="flex-1 text-center mr-[-32px]">Create new design</span>
                  )}
                </>
              )}

              {haspreviousRecipes === false && <span className="flex-1 text-center mr-[-32px]">Continue</span>}

              <ArrowRight className="h-[1em] w-[1em] fill-white " />
            </button>
          </footer>

          <ProductPreviewModal open={showPreviewModal} imageUrl={imagePreview} onClose={handlePreviewModalClose} />
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
